import { type ProColumns } from '@ant-design/pro-components'
import { Link } from '@tanstack/react-router'
import { Space, Tag } from 'antd'
import { customerSendPermissions } from '@super-mega/server/contracts/bill'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { CREATED_AT_SORT_DIRECTIONS, TABLE_ACTION_COLUMN } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import type { useMemberCreatorOptions } from '@/hooks/index.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'
import type { CustomerQuery, CustomerRow } from './customer-list-types.ts'
import { CustomerTagSelect } from './customer-tag-select.tsx'

type CustomerColumnsOptions = {
  query: CustomerQuery
  memberCreator: ReturnType<typeof useMemberCreatorOptions>
  salespersonOptions: { label: string; value: number }[]
  salespeopleLoading: boolean
  onSalespersonSearch: (keyword: string) => void
  canReadDocuments: boolean
  canSubmitOrder: boolean
  canManage: boolean
  onEdit: (customer: CustomerRow) => void
  onView: (customer: CustomerRow) => void
  onDocuments: (customer: CustomerRow) => void
  onCreateRental: (customer: CustomerRow) => void
  onCreateService: (customer: CustomerRow) => void
  onBindWecom: (customer: CustomerRow) => void
  onSend: (customer: CustomerRow) => void
  onChangeStatus: (customer: CustomerRow) => void
  onRemove: (customer: CustomerRow) => void
}
export function createCustomerListColumns({
  query,
  memberCreator,
  salespersonOptions,
  salespeopleLoading,
  onSalespersonSearch,
  canReadDocuments,
  canSubmitOrder,
  canManage,
  onEdit,
  onView,
  onDocuments,
  onCreateRental,
  onCreateService,
  onBindWecom,
  onSend,
  onChangeStatus,
  onRemove
}: CustomerColumnsOptions): ProColumns<CustomerRow>[] {
  return [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '客户名称或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },

    {
      dataIndex: 'noteKeyword',
      fieldProps: { allowClear: true, maxLength: 1000, placeholder: '多关键词用空格或逗号分隔' },
      hideInTable: true,
      title: '备注',
      tooltip: '备注包含任一关键词即可匹配'
    },
    {
      dataIndex: 'tagIds',
      hideInTable: true,
      title: '客户标签',
      tooltip: '匹配任一所选标签',
      formItemRender: () => <CustomerTagSelect />
    },
    {
      dataIndex: 'creatorMemberId',
      fieldProps: {
        allowClear: true,
        loading: memberCreator.isLoading,
        options: memberCreator.options,
        placeholder: '选择创建人',
        ...proRemoteSearch(memberCreator.onSearch)
      },
      hideInTable: true,
      title: '创建人',
      valueType: 'select'
    },
    {
      dataIndex: 'createdAtRange',
      hideInTable: true,
      title: '创建时间',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      dataIndex: 'id',
      copyable: true,
      ellipsis: true,
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      valueType: 'digit',
      fieldProps: { min: 1, precision: 0 },
      title: 'ID',
      width: '7rem',
      search: false
    },
    {
      dataIndex: 'name',
      render: (_, customer) => {
        if (!canReadDocuments) {
          return (
            <span className="block max-w-full truncate font-medium" title={customer.name}>
              {customer.name}
            </span>
          )
        }
        return (
          <Link
            to="/customer-management/customers/$customerId/documents"
            params={{ customerId: String(customer.id) }}
            search={{}}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-accent! block max-w-full truncate font-medium text-inherit! underline! underline-offset-2 transition-colors"
            title={customer.name}
          >
            {customer.name}
          </Link>
        )
      },
      search: false,
      title: '客户名称',
      width: '22rem'
    },
    {
      dataIndex: 'wecomChatId',
      title: '企微群 ID',
      search: false,
      copyable: true,
      ellipsis: true,
      width: '16rem',
      renderText: (value: string | null) => value || '—'
    },
    {
      dataIndex: 'salespersonId',
      fieldProps: {
        allowClear: true,
        loading: salespeopleLoading,
        options: salespersonOptions,
        placeholder: '输入姓名搜索业务员',
        ...proRemoteSearch(onSalespersonSearch)
      },
      hideInTable: true,
      title: '业务员',
      valueType: 'select'
    },
    {
      dataIndex: 'salesperson',
      render: (_, customer) => customer.salesperson?.name ?? '—',
      search: false,
      title: '业务员',
      width: '9rem'
    },
    {
      dataIndex: 'tags',
      render: (_, customer) =>
        customer.tags.length > 0 ? (
          <Space size={[4, 4]} wrap>
            {customer.tags.map((tag) => (
              <Tag key={tag.id} color={tag.color}>
                {tag.name}
              </Tag>
            ))}
          </Space>
        ) : (
          '—'
        ),
      search: false,
      title: '客户标签',
      width: '14rem'
    },
    {
      dataIndex: 'note',
      title: '备注',
      search: false,
      ellipsis: true,
      width: '16rem',
      renderText: (value: string | null) => value || '—'
    },
    {
      dataIndex: 'collectionBillGroupingDay',
      title: '每月归组日',
      valueType: 'digit',
      fieldProps: { min: 1, max: 28, precision: 0 },
      width: '9rem',
      render: (_, customer) =>
        customer.collectionBillGroupingDay ? `每月 ${customer.collectionBillGroupingDay} 日` : '未设置'
    },
    {
      dataIndex: 'wecomBindingStatus',
      title: '企微群绑定状态',
      hideInTable: true,
      valueType: 'select',
      valueEnum: { bound: { text: '已绑定' }, unbound: { text: '未绑定' } }
    },
    {
      dataIndex: 'autoCollectionBillGrouping',
      title: '自动每月生成账单',
      search: false,
      width: '13rem',
      render: (_, customer) =>
        customer.autoCollectionBillGrouping ? (
          <Tag color="processing">已开启 · 每月 {customer.collectionBillGroupingDay} 日</Tag>
        ) : (
          <Tag>未开启</Tag>
        )
    },
    {
      dataIndex: 'attachments',
      render: (_, customer) => `${customer.attachments.length} 个`,
      search: false,
      title: '附件',
      width: '6rem'
    },
    {
      dataIndex: 'status',
      hideInTable: true,
      title: '状态',
      valueEnum: {
        enabled: { text: '正常' },
        disabled: { text: '已停用' }
      }
    },
    {
      dataIndex: 'isDisable',
      render: (_, customer) => (
        <Tag color={customer.isDisable ? 'default' : 'success'}>{customer.isDisable ? '已停用' : '正常'}</Tag>
      ),
      search: false,
      title: '状态',
      width: '6.5rem'
    },
    {
      dataIndex: 'creator',
      render: (_, customer) => (
        <UserAvatarName avatar={customer.creator.avatar} name={customer.creator.name} size={28} />
      ),
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      sortOrder: query.sortOrder === 'asc' ? 'ascend' : 'descend',
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      ...TABLE_ACTION_COLUMN,
      render: (_, customer) => (
        <TableRowActions
          resource="customer"
          maxVisibleActions={1}
          actions={[
            ...(canManage
              ? [
                  {
                    key: 'edit',
                    label: '编辑',
                    onClick: () => onEdit(customer)
                  }
                ]
              : []),
            {
              key: 'view',
              label: '查看',
              onClick: () => onView(customer)
            },
            ...(canReadDocuments
              ? [
                  {
                    key: 'documents',
                    label: '查看单据',
                    onClick: () => onDocuments(customer)
                  }
                ]
              : []),
            ...(canSubmitOrder
              ? [
                  {
                    key: 'create-rental',
                    label: '添加租赁订单',
                    disabled: customer.isDisable,
                    onClick: () => onCreateRental(customer)
                  },
                  {
                    key: 'create-service',
                    label: '添加服务订单',
                    disabled: customer.isDisable,
                    onClick: () => onCreateService(customer)
                  }
                ]
              : []),
            {
              key: 'bind-wecom',
              label: '绑定企微群',
              permissions: ['customer.chatBinding.update'],
              disabled: customer.isDisable,
              onClick: () => onBindWecom(customer)
            },
            {
              key: 'send-collection',
              label: customer.wecomChatId ? '发送收款账单' : '发送收款账单（未绑定企微群）',
              permissions: customerSendPermissions,
              disabled: customer.isDisable || !customer.wecomChatId,
              onClick: () => onSend(customer)
            },
            ...(canManage
              ? [
                  {
                    danger: !customer.isDisable,
                    key: 'status',
                    label: customer.isDisable ? '恢复' : '停用',
                    onClick: () => onChangeStatus(customer)
                  },
                  { danger: true, key: 'delete', label: '删除', onClick: () => onRemove(customer) }
                ]
              : [])
          ]}
        />
      )
    }
  ]
}
